import { ReactNode } from 'react';
import {
  useValueStore,
  type ValueStore
} from '../../live/createValueStore';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { OpenerGroup } from '../../api/useGroups';
import { useGroupLivePosition } from '../../context/PositionsProvider';
import { GroupCostSummary } from '../../types/entry.types';
import { formatUsd, pnlColor } from '../../utils/format';

const ageLabel = (computedAt?: number): string => {
  if (computedAt == null) return '—';
  const secs = Math.max(0, Math.round((Date.now() - computedAt) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const Metric = ({
  label,
  children,
  prominent = false,
  color,
  ...rest
}: {
  label: string;
  children: ReactNode;
  prominent?: boolean;
  color?: string;
  [key: `data-${string}`]: string | undefined;
}) => (
  <Stack spacing={0.25} {...rest}>
    <Typography
      sx={{ fontSize: '0.6rem', color: 'grey.500', letterSpacing: 0.2 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: prominent ? '1.05rem' : '0.85rem',
        fontWeight: prominent ? 700 : 500,
        color: color ?? 'common.white',
        lineHeight: 1.2
      }}
    >
      {children}
    </Typography>
  </Stack>
);

/**
 * Live trading summary for ONE selected opener group. ALL FOUR figures
 * (descalce · contracts · nominal-con-fees · PnL) come from the engine's
 * `GroupCostSummary`, pushed on the `/opening` socket (`group_cost_update`) —
 * ONE source, no client-side money math (the manager's 5s REST poll is gone).
 * The legacy socket descalce (`useGroupLivePosition`) is kept ONLY as a
 * boot-grace fallback for the descalce + contract counts until the engine
 * summary arrives; the engine value is the source of truth.
 */
/**
 * A summary whose `computedAt` is older than this is treated as no-longer-live
 * (the group stopped broadcasting, e.g. it finished, or the pod stopped
 * emitting) → shown as `—` rather than a frozen last value.
 */
const STALE_MS = 15_000;

export const GroupLiveSummaryStrip = ({
  group,
  groupCost
}: {
  group: OpenerGroup;
  groupCost: GroupCostSummary | null | undefined;
}) => {
  // Legacy descalce/contracts socket — boot-grace fallback only.
  const live = useGroupLivePosition(group._id);

  const now = Date.now();
  // Only trust the engine summary when it is for THIS group. The CORE/ungrouped
  // pod (used when the group's feed pod isn't Ready, or after a feed
  // `connect_error` fallback) stamps `groupId: ''` on the aggregate it emits;
  // a stale summary from a previously selected group carries that group's id.
  // Either way `groupId !== group._id` → suppress the engine figures and fall
  // back to the group-keyed legacy descalce/contracts (and `—` for money),
  // exactly the guard the deleted REST hook applied (`money.groupId === id`).
  const costMatchesGroup = !!groupCost && groupCost.groupId === group._id;
  const costFresh = costMatchesGroup && now - groupCost!.computedAt <= STALE_MS;
  const liveFresh = !!live && now - live.computedAt <= STALE_MS;

  // Descalce + contracts: engine summary first, legacy socket as boot-grace.
  const descalce = costFresh
    ? groupCost!.descalceTotal
    : liveFresh
      ? live!.descalceTotal
      : undefined;
  const contracts = costFresh
    ? groupCost!.contractsBought
    : liveFresh
      ? live!.contractsBought
      : undefined;
  const contractsLabel = contracts
    ? `${contracts.kalshi} / ${contracts.poly} / ${contracts.total}`
    : '—';

  // Nominal + PnL: engine summary only (no legacy source).
  const pnl = costFresh ? groupCost!.livePnlUsd : undefined;

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      data-testid="group-live-summary"
      sx={{
        py: 0.75,
        px: 1.5,
        mb: 0.5,
        borderRadius: 1,
        backgroundColor: 'action.hover',
        flexShrink: 0,
        flexWrap: 'wrap'
      }}
    >
      <Metric label="Descalce" prominent color={descalce ? '#f44336' : undefined}>
        {descalce != null ? descalce.toLocaleString() : '—'}
      </Metric>

      <Metric label="Contratos (K / P / total)">{contractsLabel}</Metric>

      <Metric label="Nominal $ (con fees)">
        {costFresh ? formatUsd(groupCost!.nominalUsd) : '—'}
      </Metric>

      <Metric
        label="PnL en vivo $"
        color={pnl != null ? pnlColor(pnl) : undefined}
        data-pnl-state={
          pnl == null ? 'unknown' : pnl < 0 ? 'negative' : 'positive'
        }
      >
        {costFresh ? formatUsd(groupCost!.livePnlUsd) : '—'}
      </Metric>

      <Box sx={{ flex: 1 }} />

      {costMatchesGroup && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
            money {ageLabel(groupCost!.computedAt)}
          </Typography>
          {!costFresh && (
            <Tooltip title="The money figures are stale — the group stopped broadcasting its live summary.">
              <Chip
                label="stale"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.55rem', height: 16 }}
              />
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The strip, subscribed to the live `group_cost_update` stream.
 *
 * The summary arrives on the opener's broadcast tick — ~2/s on a live group —
 * and used to be page state, so every one of those frames re-rendered
 * `EntryPage` and through it the whole entry table, to update four numbers
 * here. Reading it from a `ValueStore` keeps the re-render local to this
 * component. `GroupLiveSummaryStrip` itself stays presentational (and its
 * tests stay value-driven).
 */
export const GroupLiveSummaryStripLive = ({
  group,
  groupCostStore
}: {
  group: OpenerGroup;
  groupCostStore: ValueStore<GroupCostSummary | null>;
}) => {
  const groupCost = useValueStore(groupCostStore);
  return <GroupLiveSummaryStrip group={group} groupCost={groupCost} />;
};
